import React, { useState } from "react";

interface PasswordInputProps extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "type"
> {
    label?: string;
    hasError?: boolean;
    errorText?: string;
}

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
    ({ label, hasError, errorText, className, ...props }, ref) => {
        const [showPassword, setShowPassword] = useState(false);

        return (
            <div className="w-full">
                <div className="flex flex-col gap-y-8">
                    {label ? (
                        <label
                            htmlFor={props.name}
                            className="font-medium text-xs text-black
                            leading-none flex items-center gap-x-4"
                        >
                            {label}
                        </label>
                    ) : null}
                    <div className="relative">
                        <input
                            ref={ref}
                            type={showPassword ? "text" : "password"}
                            className={`text-xs placeholder:text-gray
                            placeholder:text-xs placeholder:font-normal
                            text-black font-normal rounded-r8 border-0.2
                            border-strokedark shadow-active w-full
                            font-manrope outline-none
                            focus-visible:border-primary
                            disabled:cursor-not-allowed
                            disabled:opacity-80 bg-white px-12 h-40
                            pr-40
                            ${hasError ? "border-danger bg-[rgba(247,23,53,0.04)]" : ""}
                            ${className ?? ""}`}
                            {...props}
                        />
                        <button
                            type="button"
                            className="absolute right-[8px] top-1/2
                            -translate-y-1/2 flex items-center
                            bg-transparent border-none p-0
                            cursor-pointer text-gray"
                            onClick={() => setShowPassword((prev) => !prev)}
                            aria-label={
                                showPassword ? "Hide password" : "Show password"
                            }
                        >
                            {showPassword ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                                </svg>
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                </svg>
                            )}
                        </button>
                    </div>
                    {hasError && errorText ? (
                        <span className="text-danger text-xs">{errorText}</span>
                    ) : null}
                </div>
            </div>
        );
    }
);

PasswordInput.displayName = "PasswordInput";

export default PasswordInput;
